/**
 * The one gate between a `?returnUrl=` query param and `navigate()`.
 *
 * Returns the value as a same-origin path (`/path?query#hash`) or `null`.
 * react-router 8 refuses an external navigation target by throwing — so a hostile
 * `returnUrl` is not an open redirect, but it throws inside the post-login
 * `setTimeout` and strands a user who just signed in successfully (SKU-1879).
 * Callers send `null` to `/dashboard`.
 *
 * Resolution is done by the URL parser against a fixed placeholder origin rather
 * than by string checks alone, so parser quirks (`/\evil`, a tab inside `//`,
 * `javascript:`) are judged the same way the browser would judge them.
 */

const PLACEHOLDER_ORIGIN = 'https://return-path.invalid';

export function safeReturnPath(returnUrl: string | null | undefined): string | null {
  if (returnUrl == null || !returnUrl.startsWith('/')) return null;
  let resolved: URL;
  try {
    resolved = new URL(returnUrl, PLACEHOLDER_ORIGIN);
  } catch {
    return null;
  }
  if (resolved.origin !== PLACEHOLDER_ORIGIN) return null;
  // Dot segments collapse AFTER the origin is fixed: `/.//evil.example` and
  // `/a/..//evil.example` resolve to the pathname `//evil.example` on our own
  // origin — which, handed to `navigate()`, is a protocol-relative URL again.
  // The parser has already turned backslashes into slashes here.
  if (resolved.pathname.startsWith('//')) return null;
  return `${resolved.pathname}${resolved.search}${resolved.hash}`;
}
